import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import MUIButton from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";
import EducationalResourcesFilters from "@/ui/content/educationalResources/Filters";
import EducationalResourceModal from "@/ui/modals/educationalResources/EducationalResourceModal";
import {
  useDeleteEducationalResource,
  useEducationalResources,
} from "@/hooks/useEducationalResources";
import { useEducationalResourceAudit } from "@/hooks/useEducationalResourceAudit";
import { useCategoryByType } from "@/hooks/useCategories";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { ErrorResponse } from "@/domain/error.interface";
import {
  EducationalResource,
  EducationalResourceFilters,
  EducationalResourceSortField,
  RESOURCE_STATUS_LABELS,
  RESOURCE_TYPE_LABELS,
  ResourceCategoryItem,
  categoryItemName,
  resourceDisplayTitle,
} from "@/domain/educationalResource.interface";

const TID = "educational-resources";

const INITIAL_FILTERS: EducationalResourceFilters = {
  search: "",
  categoryId: "",
  type: "",
  status: "",
  pageNumber: 0,
  pageSize: 10,
  sortField: "createdAt",
  sortOrder: "desc",
};

const SORTABLE: EducationalResourceSortField[] = [
  "createdAt",
  "type",
  "status",
];

const errorMessage = (error: unknown): string => {
  const err = (error || {}) as Partial<ErrorResponse>;
  const message = Array.isArray(err.message) ? err.message[0] : err.message;
  return message || "An error occurred";
};

const headCells: HeadCell[] = [
  {
    id: "title",
    isSortable: false,
    disablePadding: true,
    label: "Title",
    align: "left",
  },
  {
    id: "category",
    isSortable: false,
    disablePadding: false,
    label: "Category",
    align: "left",
  },
  {
    id: "type",
    isSortable: true,
    disablePadding: false,
    label: "Type",
    align: "left",
    width: "140px",
  },
  {
    id: "languages",
    isSortable: false,
    disablePadding: false,
    label: "Languages",
    align: "left",
  },
  {
    id: "status",
    isSortable: true,
    disablePadding: false,
    label: "Status",
    align: "left",
    width: "130px",
  },
  {
    id: "action",
    isSortable: false,
    disablePadding: false,
    label: "Actions",
    align: "right",
    width: "100px",
  },
];

const EducationalResourcesPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const audit = useEducationalResourceAudit();

  const filtersState = useState<EducationalResourceFilters>(INITIAL_FILTERS);
  const [filters, setFilters] = filtersState;

  const [modalState, setModalState] = useState<{
    open: boolean;
    resource?: EducationalResource;
  }>({ open: false });
  const [deleteState, setDeleteState] = useState<{ id?: string }>({});

  const {
    educationalResources,
    isLoadingEducationalResources,
    educationalResourcesError,
  } = useEducationalResources(filters);

  // All items (active and inactive): the table must name a resource's category even after the
  // item is deactivated. A distinct `action` keeps this out of the ACTIVE-only cache entries.
  const { category, isLoadingCategory } = useCategoryByType({
    type: "EDUCATIONAL_RESOURCE",
    status: "ALL",
    action: "educational-resources-all-items",
  });
  const categoryItems: ResourceCategoryItem[] = useMemo(
    () => (category?.categories as ResourceCategoryItem[] | undefined) || [],
    [category]
  );
  const categoryNames = useMemo(() => {
    const map = new Map<string, string>();
    categoryItems.forEach((item) => map.set(item._id, categoryItemName(item)));
    return map;
  }, [categoryItems]);
  const activeCategoryOptions = useMemo(
    () =>
      categoryItems
        .filter((item) => item.status !== "INACTIVE")
        .map((item) => ({ key: item._id, value: categoryItemName(item) })),
    [categoryItems]
  );

  const deleteResource = useDeleteEducationalResource();

  useEffect(() => {
    setPageTitle("Educational Resources");
  }, []);

  // AC7: one LIST_VIEW entry when the list is opened.
  const viewLogged = useRef(false);
  useEffect(() => {
    if (!viewLogged.current && userInfo?._id) {
      viewLogged.current = true;
      audit("LIST_VIEW");
    }
  }, [userInfo?._id, audit]);

  // AC7: a SEARCH entry per (already debounced) non-empty search. The text itself is not logged.
  useEffect(() => {
    if (filters.search) audit("SEARCH");
  }, [filters.search]);

  useEffect(() => {
    if (educationalResourcesError) {
      showSnackBar(errorMessage(educationalResourcesError), "error");
    }
  }, [educationalResourcesError]);

  const handleConfirmDelete = async () => {
    const id = deleteState.id;
    if (!id) return;
    try {
      await deleteResource.mutateAsync({ id, updatedBy: userInfo?._id });
      audit("DELETE", id);
      showSnackBar("The educational resource has been deleted.", "success");
      setDeleteState({});
    } catch (error: unknown) {
      showSnackBar(errorMessage(error), "error");
    }
  };

  const rows = useMemo(
    () =>
      (educationalResources?.list || []).map((resource) =>
        createDataRow(resource._id, {
          title: (
            <Typography variant="bodyMedium" component="div">
              {resourceDisplayTitle(resource)}
            </Typography>
          ),
          category: (
            <Typography variant="bodyMedium" component="div">
              {categoryNames.get(resource.categoryId) || "-"}
            </Typography>
          ),
          type: (
            <div>{RESOURCE_TYPE_LABELS[resource.type] || resource.type}</div>
          ),
          languages: (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {(resource.languages || []).map((l) => (
                <Chip
                  key={l.language}
                  size="small"
                  variant="outlined"
                  label={
                    <div className="text-xs">{l.language.toUpperCase()}</div>
                  }
                />
              ))}
            </Stack>
          ),
          status: (
            <Chip
              size="small"
              color={resource.status === "ACTIVE" ? "success" : "default"}
              label={
                <div className="text-xs">
                  {RESOURCE_STATUS_LABELS[resource.status] || resource.status}
                </div>
              }
            />
          ),
          action: (
            <ActionMenu
              id={resource._id}
              dataTestId={`${TID}-row-menu-button-${resource._id}`}
              theme={theme}
              options={[
                {
                  value: "Edit",
                  onClick: () => setModalState({ open: true, resource }),
                  icon: <EditIcon fontSize="small" />,
                  dataTestId: `${TID}-row-edit-menuitem`,
                },
                {
                  value: "Delete",
                  onClick: () => setDeleteState({ id: resource._id }),
                  icon: <DeleteOutlineIcon fontSize="small" />,
                  dataTestId: `${TID}-row-delete-menuitem`,
                },
              ]}
            />
          ),
        })
      ),
    [educationalResources, categoryNames, theme]
  );

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    variant="headlineSmall"
                    component="h1"
                    data-testid={`${TID}-page-title`}
                  >
                    Educational Resources
                  </Typography>

                  <MUIButton
                    size="medium"
                    onClick={() => setModalState({ open: true })}
                    variant="contained"
                    data-testid={`${TID}-add-button`}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </MUIButton>
                </Box>

                <EducationalResourcesFilters
                  filtersState={filtersState}
                  categoryOptions={activeCategoryOptions}
                  isLoadingCategories={isLoadingCategory}
                />

                <Box data-testid={`${TID}-table`}>
                  <DataTable
                    data={rows}
                    headCells={headCells}
                    order={filters.sortOrder}
                    orderBy={filters.sortField}
                    rowsPerPage={filters.pageSize}
                    page={filters.pageNumber}
                    total={educationalResources?.totalCount}
                    isLoading={isLoadingEducationalResources}
                    messageEmptyData="No educational resources to show."
                    onChangePage={(_event, newPage) =>
                      setFilters((prev) => ({ ...prev, pageNumber: newPage }))
                    }
                    onChangeSize={(event) =>
                      setFilters((prev) => ({
                        ...prev,
                        pageNumber: 0,
                        pageSize: Number(event.target.value),
                      }))
                    }
                    onChangeSort={(order, orderBy) => {
                      const field = String(
                        orderBy
                      ) as EducationalResourceSortField;
                      if (!SORTABLE.includes(field)) return;
                      setFilters((prev) => ({
                        ...prev,
                        pageNumber: 0,
                        sortField: field,
                        sortOrder: order === "asc" ? "asc" : "desc",
                      }));
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <EducationalResourceModal
          open={modalState.open}
          onClose={() => setModalState({ open: false })}
          resource={modalState.resource}
          categories={categoryItems}
          isLoadingCategories={isLoadingCategory}
        />
      )}

      {deleteState.id && (
        <GenericAlertModal
          title="Delete educational resource"
          description="Are you sure you want to delete this educational resource?"
          theme={theme}
          onCancel={() => setDeleteState({})}
          onClick={handleConfirmDelete}
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={deleteResource.isPending}
          dataTestId={`${TID}-delete-dialog`}
          submitTestId={`${TID}-delete-confirm-button`}
          cancelTestId={`${TID}-delete-cancel-button`}
        />
      )}
    </>
  );
};

export default EducationalResourcesPage;
